/* global axios */
import ApiClient from '../ApiClient';

class KanbanClient extends ApiClient {
  constructor() {
    super('kanban/funnels', { accountScoped: true });
  }

  // Funnels
  getFunnels(params = {}) {
    return axios.get(this.url, { params });
  }

  getFunnel(funnelId) {
    return axios.get(`${this.url}/${funnelId}`);
  }

  createFunnel(data) {
    return axios.post(this.url, { funnel: data });
  }

  updateFunnel(funnelId, data) {
    return axios.patch(`${this.url}/${funnelId}`, { funnel: data });
  }

  deleteFunnel(funnelId) {
    return axios.delete(`${this.url}/${funnelId}`);
  }

  // Stages
  getStages(funnelId) {
    return axios.get(`${this.url}/${funnelId}/stages`);
  }

  createStage(funnelId, data) {
    return axios.post(`${this.url}/${funnelId}/stages`, { stage: data });
  }

  updateStage(funnelId, stageId, data) {
    return axios.patch(`${this.url}/${funnelId}/stages/${stageId}`, {
      stage: data,
    });
  }

  deleteStage(funnelId, stageId) {
    return axios.delete(`${this.url}/${funnelId}/stages/${stageId}`);
  }

  reorderStages(funnelId, stageIds) {
    return axios.post(`${this.url}/${funnelId}/stages/reorder`, {
      stage_ids: stageIds,
    });
  }

  // Legacy Conversations on Kanban (keep for backward compat)
  getConversations(funnelId) {
    return axios.get(`${this.url}/${funnelId}/conversations`);
  }

  addConversation(funnelId, data) {
    return axios.post(`${this.url}/${funnelId}/conversations`, {
      conversation: data,
    });
  }

  moveConversation(funnelId, kanbanConversationId, stageId) {
    return axios.patch(
      `${this.url}/${funnelId}/conversations/${kanbanConversationId}/move`,
      { stage_id: stageId }
    );
  }

  updateConversation(funnelId, kanbanConversationId, data) {
    return axios.patch(
      `${this.url}/${funnelId}/conversations/${kanbanConversationId}`,
      { conversation: data }
    );
  }

  removeConversation(funnelId, kanbanConversationId) {
    return axios.delete(
      `${this.url}/${funnelId}/conversations/${kanbanConversationId}`
    );
  }

  lookupConversation(conversationId) {
    const baseUrl = this.url.replace('/kanban/funnels', '/kanban');
    return axios.get(`${baseUrl}/conversations/${conversationId}/lookup`);
  }

  // ---- Deals (CRM) ----
  getDeals(funnelId, params = {}) {
    return axios.get(`${this.url}/${funnelId}/deals`, { params });
  }

  getDeal(funnelId, dealId, options = {}) {
    return axios.get(`${this.url}/${funnelId}/deals/${dealId}`, options);
  }

  getDealAttachments(funnelId, dealId, beforeId, options = {}) {
    return axios.get(`${this.url}/${funnelId}/deals/${dealId}/attachments`, {
      ...options,
      params: beforeId ? { before_id: beforeId } : {},
    });
  }

  getDealStatistics(funnelId, dealId, options = {}) {
    return axios.get(
      `${this.url}/${funnelId}/deals/${dealId}/statistics`,
      options
    );
  }

  exportDeal(funnelId, dealId, options = {}) {
    return axios.get(`${this.url}/${funnelId}/deals/${dealId}/export`, {
      ...options,
      responseType: 'blob',
    });
  }

  getDealStageMovements(funnelId, dealId, page, options = {}) {
    return axios.get(
      `${this.url}/${funnelId}/deals/${dealId}/stage_movements`,
      {
        ...options,
        params: { page },
      }
    );
  }

  createDeal(funnelId, data) {
    return axios.post(`${this.url}/${funnelId}/deals`, { deal: data });
  }

  updateDeal(funnelId, dealId, data) {
    return axios.patch(`${this.url}/${funnelId}/deals/${dealId}`, {
      deal: data,
    });
  }

  deleteDeal(funnelId, dealId) {
    return axios.delete(`${this.url}/${funnelId}/deals/${dealId}`);
  }

  moveDeal(funnelId, dealId, stageId, lockVersion) {
    return axios.patch(`${this.url}/${funnelId}/deals/${dealId}/move`, {
      stage_id: stageId,
      lock_version: lockVersion,
    });
  }

  lookupDealsByContact(contactId, params = {}) {
    const baseUrl = this.url.replace('/kanban/funnels', '/kanban');
    return axios.get(`${baseUrl}/deals/by_contact/${contactId}`, { params });
  }

  lookupDealsByConversation(conversationId) {
    const baseUrl = this.url.replace('/kanban/funnels', '/kanban');
    return axios.get(`${baseUrl}/deals/by_conversation/${conversationId}`);
  }

  // Deal Metrics
  getDealSummary(params = {}) {
    const baseUrl = this.url.replace('/kanban/funnels', '/kanban');
    return axios.get(`${baseUrl}/deal_metrics/summary`, { params });
  }

  getDealsByStage(params = {}) {
    const baseUrl = this.url.replace('/kanban/funnels', '/kanban');
    return axios.get(`${baseUrl}/deal_metrics/by_stage`, { params });
  }

  getDealsByPeriod(params = {}) {
    const baseUrl = this.url.replace('/kanban/funnels', '/kanban');
    return axios.get(`${baseUrl}/deal_metrics/by_period`, { params });
  }

  // ---- Products ----
  getProducts() {
    const baseUrl = this.url.replace('/kanban/funnels', '/kanban');
    return axios.get(`${baseUrl}/products`);
  }

  createProduct(data) {
    const baseUrl = this.url.replace('/kanban/funnels', '/kanban');
    return axios.post(`${baseUrl}/products`, { product: data });
  }

  updateProduct(productId, data) {
    const baseUrl = this.url.replace('/kanban/funnels', '/kanban');
    return axios.patch(`${baseUrl}/products/${productId}`, { product: data });
  }

  deleteProduct(productId) {
    const baseUrl = this.url.replace('/kanban/funnels', '/kanban');
    return axios.delete(`${baseUrl}/products/${productId}`);
  }
}

export default new KanbanClient();
